import type { Messages } from './en';

/** 第四階段內容：教室（繁體中文・台灣）。`phrases` 與 `contest.words` 是英文練習，保留英文。 */
export const zhTWClassroom: Pick<Messages, 'classroom'> = {
  classroom: {
    mapNames: {},
    zones: {},
    objects: {
      cls_lostBox: '失物招領箱',
      cls_notice: '公布欄',
      cls_ball: '操場上的球',
    },
    npc: {
      xiaohua: '小華',
      xiaomei: '小美',
      azhe: '阿哲',
      xiaoqiang: '小強',
      xiaofang: '小芳',
    },
    items: {},

    /** 英文練習：保留英文。 */
    phrases: {
      late: 'Sorry I’m late. It won’t happen again.',
      homework: 'Excuse me, could you help me? I don’t understand this part.',
      standing: 'I’m sorry for talking. I’ll raise my hand next time.',
      ball: 'I feel sad when I don’t get a turn.',
      forgotHomework: 'I’m sorry, I forgot my homework. I’ll bring it next time.',
      misunderstanding: 'Excuse me, I think there’s a misunderstanding.',
      bullying: 'Stop, please. That’s not kind.',
      contest: 'Could you repeat the word, please?',
    },

    ui: {
      sayIt: '🔊 用英文說說看',
      practised: '🗣️ 英文句子練習完成！',
      journalMenu: '📔 成長紀錄',
      journalTitle: '📔 成長紀錄',
      tabPeople: '朋友與家人',
      tabLog: '點點滴滴',
      tabHobbies: '興趣',
      family: '家人',
      classmates: '同學與朋友',
      noLog: '還沒有寫下任何事。去好好過每一天吧——善良的選擇、勇敢的時刻，就連犯過的錯，都會記在這裡。',
      noHobbies: '還沒有練習任何興趣。試試鋼琴、畫架、操場或圖書館吧！',
      hobbyLevel: (n: number) => `Lv ${n}`,
      hobbyPoints: (n: number) => `${n} 次`,
      dayLabel: (week: number, day: string) => `第 ${week} 週・${day}`,
      todayRow: '今天的點點滴滴',
      noticeTitle: '📌 公布欄',
      noticeNone: '今天沒有特別的事。祝你在學校度過美好的一天！',
      noticeCases: '今天可能有同學需要朋友幫忙喔。多留意一下！',
      lateGone: '小華已經進教室了。等一下你還是可以對他好一點。',
      later: '等一下再聊',
      back: '‹ 返回',
      tryAgain: '嗯……換個方法試試看。',
      narrator: '這時候……',
      ballIdle: '一顆磨舊的足球，上面用麥克筆寫著「小強」。一定是小強忘在這裡的。',
      chat: '💬 聊天',
      giveBack: (what: string) => `🎁 把${what}還回去`,
      coolHello: ['喔……嗨。', '嗯，你好。', '……嗨。'],
      warmHello: ['嘿，好麻吉！', '耶，是你！', '你來啦！我正希望能見到你。'],
    },

    love: ['陌生人', '慢慢認識', '朋友', '好朋友', '最好的朋友'],
    loveFamily: ['有點疏遠', '還好', '親近', '非常親近', '形影不離'],

    hobbies: {
      music: '🎵 音樂', art: '🎨 美術', sport: '⚽ 運動', reading: '📚 閱讀', nature: '🌿 大自然', craft: '✂️ 手作',
      cooking: '🍳 料理', garden: '🌱 園藝', english: '🗣️ 英文', dance: '💃 舞蹈',
    },

    journalKeys: {
      helpedLate: () => '幫小華為遲到道歉，還教他一招鬧鐘妙方',
      lateExcuse: () => '叫小華編個藉口。結果不太順利。',
      teasedLate: () => '小華遲到時，我笑了他',
      helpedHomework: () => '陪小美誠實地請老師幫忙',
      copiedHomework: () => '讓小美抄我的作業。她還是不懂。',
      skippedHomework: () => '叫小美假裝生病',
      helpedStanding: () => '幫阿哲冷靜下來，向林老師好好道歉',
      clowned: () => '上課時跟阿哲一起扮鬼臉——結果我們兩個都被念了',
      mockedStanding: () => '對阿哲說「活該」',
      ballMadeUp: () => '幫小強和小芳用「我覺得……」來說感受，並且輪流玩',
      ballSide: (who?: string) => `搶球吵架時，我站在${who ?? '其中一個人'}那邊`,
      ballGrabbed: () => '把球搶走跑掉了。沒有人笑。',
      madeUp: (who?: string) => `向${who ?? '朋友'}道歉，我們和好了`,
      forgotHonest: () => '忘了帶作業，並跟老師說實話',
      forgotExcuse: () => '忘了帶作業，還編了藉口',
      homeworkMadeUp: () => '下課時把沒寫的作業補完了',
      lostFound: () => '鉛筆盒不見了……結果在失物招領箱找到了',
      returnedItem: (who?: string) => `把${who ?? '同學'}弄丟的東西還給他`,
      calmExplained: () => '冷靜地向林老師解釋了一場誤會',
      shouted: () => '因為一場誤會對林老師大吼',
      bullyStood: () => '大孩子嘲笑小寶時，我站在他身邊',
      bullyTold: () => '小寶被嘲笑時，我去告訴大人',
      bullyIgnored: () => '小寶被嘲笑時，我走開了',
      contestWin: () => '贏得班上的拼字比賽！',
      contestSetback: () => '拼字比賽還沒贏——還沒而已。我會繼續練習。',
      quiz: (grade?: string) => `隨堂小考：${grade ?? '?'} 等`,
      meeting: (how?: string) => (how === 'great' ? '家長會：林老師說了好多我的優點' : how === 'mixed' ? '家長會：有好消息，也有要加油的地方' : '家長會：媽媽和我一起訂了計畫'),
    },

    kids: {
      xiaohua: {
        hello: ['嗨！我遲到了嗎？……喔，呼。還沒上課嘛。', '嘿！你睡得好嗎？我每次都睡得太好了。'],
        topics: [
          { say: '我阿公說早起的鳥兒有蟲吃。我說早起的鳥兒很想睡。', choices: [{ t: '哈哈！試試早點睡？', reply: '嗯……如果我不再躲在被子裡看漫畫的話，也許可以。' }, { t: '你喜歡看什麼？', reply: '太空貓的漫畫！艦長是一隻叫麻糬的貓。' }] },
          { say: '我在存錢買火箭模型組，還有真的發射台喔！', choices: [{ t: '好酷！做好了可以給我看嗎？', reply: '可以！你來倒數：三、二、一……' }, { t: '還差多少錢？', reply: '還要兩個禮拜不買糖果。唉。' }] },
        ],
        react: {
          alarm: '你猜怎樣？我昨晚設了兩個鬧鐘，還先把書包整理好。今天我提早到了！謝謝你的妙方！',
          excuse: '藉口沒有用……林老師都知道。下次我就直接說實話。',
          teased: '……喔，是你。請不要再笑我了。那讓我覺得自己好渺小。',
          madeUp: '我們沒事了。謝謝你跟我道歉——你很棒。',
        },
        apologise: '為笑他的事道歉',
        apologyReply: '好……謝謝。現在沒事了。要不要看我的太空貓漫畫？',
      },
      xiaomei: {
        hello: ['喔！嗨……我剛剛在看書。', '你好。你也喜歡圖書館嗎？'],
        topics: [
          { say: '我看完一本書，講一個會跟鯨魚說話的女孩。結局我哭了。這樣很奇怪嗎？', choices: [{ t: '一點都不奇怪。好書就是這樣。', reply: '謝謝……你想看的話我可以借你。' }, { t: '鯨魚真的會「說話」喔！', reply: '真的嗎？牠們會唱歌，歌聲可以傳得好遠。我也讀過！' }] },
          { say: '我在練習變得更勇敢。今天我跟公車司機說了「早安」。', choices: [{ t: '好勇敢！一小步也算數。', reply: '他也跟我說早安！我好開心。' }, { t: '下次用英文說！', reply: 'Good… morning！嘻嘻，我做到了。' }] },
        ],
        react: {
          asked: '林老師教了我第 3 題。我現在懂了！開口問很可怕……可是很值得。',
          copied: '我還是不懂第 3 題……抄作業沒有讓我學會。下次你可以直接教我嗎？',
          skipped: '我躲在保健室，可是我根本沒生病。我覺得更糟了。',
          madeUp: '謝謝你跟我道歉。我們找時間一起讀書好嗎？',
        },
        apologise: '為出了壞主意道歉',
        apologyReply: '沒關係。我們都學到了一課。下次一起寫作業好嗎？',
      },
      azhe: {
        hello: ['喲！想聽笑話嗎？……等一下再說。我在練習自制力。嘿嘿。', '嘿嘿！最近怎樣？'],
        topics: [
          { say: '為什麼那個學生把作業吃掉了？因為老師說那是「小菜一碟」！', choices: [{ t: '哈哈！好冷喔。但我喜歡。', reply: '我還有一百個。留到下課再講！' }, { t: '再講一個！', reply: '什麼東西有四個輪子又有一堆蒼蠅？垃圾車！哈哈哈！' }] },
          { say: '我長大想當電視節目主持人。說話是我的超能力！', choices: [{ t: '你一定會很厲害！', reply: '對吧？不過好的主持人也要知道什麼時候該聽。這部分我還在學。' }, { t: '可是超能力也要控制得住喔。', reply: '哈，林老師也這樣說。你們兩個可以開個社團了。' }] },
        ],
        react: {
          calm: '我今天舉了三次手，每次都有等。林老師給了我一張貼紙！',
          clowned: '昨天我們兩個都被念了，哈。鬼臉還是留到下課再扮吧。',
          mocked: '哼。說「活該」真的不太好耶。',
          madeUp: '沒事啦，朋友！大家有時候都會說錯話。我最常！',
        },
        apologise: '為說了不好聽的話道歉',
        apologyReply: '接受道歉！擊個掌？✋',
      },
      xiaoqiang: {
        hello: ['嘿！下課要不要一起踢球？', '喲！我昨天在球場進了三球！'],
        topics: [
          { say: '我以後要當職業足球員。不過教練說我要多傳球。', choices: [{ t: '傳球會讓整隊都變強！', reply: '好啦好啦……我在練了。其實小芳傳球超厲害的。' }, { t: '可以教我一招嗎？', reply: '好啊！踩住球、往後拉、轉身。超簡單！' }] },
          { say: '我每個禮拜天都跟我爸看棒球。他會對著電視大叫，超好笑。', choices: [{ t: '你最喜歡哪一隊？', reply: '兄弟！黃色永遠支持！' }, { t: '電視有沒有吼回去？', reply: '哈哈！沒有，可是我媽會！' }] },
        ],
        react: {
          madeUp: '我跟小芳現在會輪流，一人五分鐘。老實說？兩個人玩比較好玩。',
          sideMe: '謝謝你昨天幫我說話……可是小芳現在不理我了。有點難過。',
          sideOther: '你昨天站在小芳那邊。我到現在還有點不開心。',
          grabbed: '搶了我的球就跑，這樣不好。那不是拿來捉弄人的玩具。',
          madeUpYou: '好，我們又和好了。謝謝你道歉。',
        },
        apologise: '道歉並好好談一談',
        apologyReply: '好啦。我也有不對的地方。下次大家一起玩吧。',
      },
      xiaofang: {
        hello: ['嗨！你今天早上有看到雲嗎？有一朵好像兔子。', '嘿！我在練自由球。要不要看？'],
        topics: [
          { say: '有人說足球是男生的運動。好好笑喔。球才不管是誰在踢。', choices: [{ t: '對啊！你踢得超好。', reply: '謝謝！我每天上學前都會練習。' }, { t: '找時間一起踢吧！', reply: '一言為定！我教你我的香蕉球。' }] },
          { say: '我生氣的時候，會數到十、握緊拳頭，然後放開。很有用……大部分時候啦。', choices: [{ t: '這招好棒。', reply: '是我姊姊教我的。她說情緒就像海浪——會過去的。' }, { t: '我會深呼吸。', reply: '喔，我也來試試。吸氣……吐氣。' }] },
        ],
        react: {
          madeUp: '輪流比吵架好太多了。而且小強跟我道歉了！這從來沒發生過！',
          sideMe: '謝謝你幫我說話。可是……下次也許可以幫我們兩個，好嗎？',
          sideOther: '你站在小強那邊。我還以為你是我的朋友。',
          grabbed: '那一點都不好笑。我們本來就已經很難過了。',
          madeUpYou: '謝謝你道歉。我們又是朋友了！',
        },
        apologise: '道歉並好好談一談',
        apologyReply: '好。謝謝你回來跟我說。這需要勇氣。',
      },
    },

    react: {
      teacher: {
        honest: '再次謝謝你跟我說作業的實話。誠實會讓一切變得更簡單。',
        calm: '前幾天的誤會我還是覺得很抱歉。你解釋得那麼冷靜——我也從你身上學到了。',
        helped: '我注意到你常常幫助同學。這就是好朋友會做的事。',
      },
      bo: {
        stood: '謝謝你在公園站在我旁邊。現在看到那些人，我覺得自己勇敢多了。',
        told: '春伯跟那些大孩子談過了。他們跟我道歉了！謝謝你去告訴他。',
        ignored: '我看到你在公園走開了……沒關係。只是……下次可以陪著我嗎？',
      },
      mom: {
        meeting: '想到林老師在家長會說的話，我到現在還在笑呢。',
      },
      festival: (name: string) => `${name}快樂！這個禮拜好好玩對不對？`,
      trip: (place: string) => `聽說你去了${place}！你最喜歡哪個部分？`,
      typhoon: '好大的颱風！待在家裡，注意安全喔！',
      rain: '今天雨好大！你有帶傘嗎？',
    },

    cases: {
      late: {
        open: '完了完了！我睡過頭，鐘聲已經響了！林老師一定會很生氣……我乾脆躲在廁所等到下課好了？',
        good: '躲起來只會更糟。我們一起進去道歉吧。',
        excuse: '就說公車遲到了。',
        tease: '哈哈！你遲到超久的！',
        how: '可是……我要說什麼？',
        howGood: '道歉、說實話，然後保證會更努力。',
        howQuiet: '偷偷溜進去。也許她不會發現。',
        quietReply: '偷溜感覺好可怕……萬一被她看到怎麼辦？我覺得直接說比較好。',
        phraseIntro: '我們一起用英文練習，就像林老師教我們的那樣：',
        plan: '好，我會說的！可是下次要怎麼樣才不會遲到？',
        planAlarm: '設個鬧鐘，把鬧鐘放得離床遠一點。',
        planBag: '前一天晚上就把書包整理好。',
        planBoth: '兩個都做！鬧鐘設好，書包也放在門口。',
        planReply: '好棒的計畫。我今天晚上就來試試！',
        teacher: '謝謝你誠實告訴我，小華。每個人都有遲到的時候——重要的是接下來怎麼做。進來坐下吧。',
        thanks: '呼……沒有想像中那麼糟。謝謝你幫我！',
        excuseEnd: '小華跟林老師說公車遲到了。老師平靜地說，她看到他的公車準時到站。現在他得解釋兩次……藉口通常只會讓事情更麻煩。',
        teaseEnd: '小華低頭看著地板，一個人走進教室。他的耳朵紅通通的。如果是你，會有什麼感覺？',
      },
      homework: {
        open: '我……我數學作業沒寫完。第 3 題我看不懂，所以就……停下來了。如果我進去，大家都會看到。',
        good: '看不懂沒關係。我們誠實地去請林老師幫忙吧。',
        copy: '快，抄我的！',
        sick: '去保健室說你不舒服。',
        scared: '可是萬一她生氣怎麼辦？萬一我說錯話呢？',
        brave: '說「我不會」是很勇敢的事。老師喜歡大家發問。',
        withYou: '我陪你去。你不會是一個人的。',
        braveReply: '勇敢……好。我可以很勇敢。',
        withYouReply: '真的嗎？謝謝你……這樣就容易多了。',
        phraseIntro: '你可以這樣說——用英文練習一下：',
        teacher: '謝謝你告訴我，小美。不懂，就是學習的開始。下課我們一起來看第 3 題吧。',
        thanks: '她一點都沒有生氣！還好我有問。謝謝你當我的朋友。',
        copyEnd: '小美抄了你的答案。上課時，林老師請她解釋第 3 題……她說不出來。她靜靜地看著你。抄作業沒有讓她學會。',
        sickEnd: '護理師溫柔地幫小美檢查，說她沒有生病。小美錯過了那堂課，還是看不懂第 3 題。',
      },
      standing: {
        open: '噓！林老師叫我站到後面，因為我一直講話。好不公平！嘿——要不要看我扮鬼臉？😜',
        good: '現在不行。我們別再惹麻煩了。下課再跟我聊！',
        clown: '也扮鬼臉回去。',
        mock: '活該。',
        why: '……好啦。我不是故意要講話的。我只是太興奮，話就自己跑出來了！',
        tipWrite: '想到點子的時候，先寫下來，等一下再跟我說。',
        tipBreathe: '深呼吸，然後舉手。等輪到你。',
        tipReply: '咦，這好像真的有用。就像一本祕密笑話筆記本！',
        fix: '我應該跟林老師說些什麼嗎？',
        fixGood: '對。向她道歉，然後告訴她你的計畫。',
        fixLater: '也許等她忘記就好了。',
        fixLaterReply: '嗯……我覺得她不會忘記。還是把事情處理好比較好。我們去吧。',
        phraseIntro: '我們一起用英文說：',
        teacher: '謝謝你，阿哲。我看得出來你真的很努力。請坐下吧——輪到你的時候，我很想聽聽你的想法。',
        thanks: '耶！自制力：第一級！謝啦，兄弟。',
        clownEnd: '林老師轉過身，看到你們兩個都在扮鬼臉。「現在變兩個了？」她平靜地說。你們兩個都站到下課。一點都不好笑了。',
        mockEnd: '阿哲的笑容不見了。「哇，真是謝謝你喔。」他小聲說完，把頭轉開。',
      },
      ball: {
        open: '「那是我的球！是我帶來的！」小強大喊。\n「可是你整節下課都在玩！該我了！」小芳大喊。',
        calm: '嘿，大家先冷靜一下。你們可以各自說說自己的感受嗎？',
        sideQiang: '「小強說得對。那是他的球。」',
        sideFang: '「小芳說得對。要分享！」',
        grab: '把球搶走就跑！',
        iIntro: '試試這樣說：「當……的時候，我覺得……，因為……」。哪一句是「我訊息」？',
        iGood: '「輪不到我的時候，我覺得很難過，因為我也想玩。」',
        iBad1: '「你超自私的！」',
        iBad2: '「你每次都把事情搞砸！」',
        iBadReply: '這句是在怪對方——只會吵得更兇。「我訊息」是說出「你自己」的感受。',
        iReply: '小芳深吸一口氣，說了出來。小強眨了眨眼。「喔……我不知道你會難過。」',
        phraseIntro: '這是那句「我訊息」的英文——跟她一起說：',
        solve: '那……我們現在要怎麼辦？',
        turns: '輪流玩：一人五分鐘。',
        together: '一起玩！互相傳球。',
        solveReply: '「好啦……對不起我剛剛大吼。」小強說。「我也對不起。」小芳說。「要不要一起玩？」',
        thanks: '謝謝你幫我們。這比吵架好多了！',
        sideQiangEnd: '小強咧嘴一笑，抱著球跑走了。小芳眼眶泛淚。「好啊，隨便。」她一個人走開了。',
        sideFangEnd: '小強把球往地上一丟。「好啦，給你！你們都跟我作對！」他氣呼呼地走了。',
        grabEnd: '你抱著球跑走了。沒有人笑。現在小強和小芳不只生彼此的氣，也生你的氣。',
      },
    },

    events: {
      forgotHomework: {
        ask: '各位早安。請把上禮拜的作業交上來……咦？你的作業呢？',
        honest: '對不起，我忘記帶了。',
        excuse: '被我家的狗吃掉了！',
        honestReply: '謝謝你說實話。這是最重要的。你可以下課時寫，或是下次帶來。',
        excuseReply: '嗯。我比較想聽實話——我不會生氣的。你是不是忘了？',
        admit: '對……我忘了。對不起。',
        admitReply: '謝謝你。你看，說實話心裡比較輕鬆吧。',
        makeUp: '📝 下課馬上補寫',
        later: '下次帶來',
        doneReply: '做得好！謝謝你把事情處理好。',
      },
      lostPencil: {
        start: '😟 糟糕！你的鉛筆盒不在書包裡。去走廊的失物招領箱看看吧？',
        found: '找到了——你的鉛筆盒，標籤上還寫著你的名字！一定是有好心人送過來的。',
        idle: '一箱失物：一隻孤單的手套、一個水壺、三個橡皮擦……',
        other: (what: string, who: string) => `這裡有${what}，上面寫著名字：「${who}」。`,
        give: (who: string) => `拿去給${who}`,
        leave: '先放著',
        thanks: '我不見的東西！非常謝謝你拿來給我！',
        items: { eraser: '一個恐龍橡皮擦', bottle: '一個藍色水壺', ruler: '一把亮晶晶的尺', bookmark: '一張貓咪書籤', keychain: '一個小熊鑰匙圈' },
        carrying: (what: string, who: string) => `你正幫${who}拿著${what}。`,
      },
      misunderstanding: {
        ask: '有人把洗筆水打翻在美術教室，就這樣丟著不管。是你嗎？我很失望。',
        calm: '保持冷靜，好好解釋。',
        shout: '太不公平了！你每次都怪我！',
        silent: '什麼都不說，生悶氣。',
        explain: '那時候我在圖書館，不在美術教室。不過我可以幫忙清理。',
        calmReply: '喔……你說得對，圖書館阿姨有看到你。對不起，我太快下結論了。謝謝你這麼冷靜地解釋——也謝謝你願意幫忙。',
        shoutReply: '我知道你很難過。可是你用吼的，我很難好好聽。你可以冷靜地告訴我嗎？',
        silentReply: '你不說，我就不知道發生了什麼事。深呼吸一下——我在聽。',
        tryCalm: '深呼吸，冷靜地解釋。',
      },
      bullying: {
        start: '😢 你聽到公園的風箏樹旁邊有人在哭……',
        open: '兩個大孩子在嘲笑小寶的風箏。「寶寶風箏！寶寶風箏！」小寶強忍著眼淚。',
        stand: '站到小寶身邊，冷靜地說：「請停下來。這樣不友善。」',
        tell: '去告訴大人（春伯或小寶的媽媽）。',
        fight: '推他們！',
        ignore: '走開，假裝沒看到。',
        fightReply: '打架只會讓事情更糟——可能會有人受傷，而且你也會惹上麻煩。還有更勇敢的方法。',
        standReply: '大孩子們嚇了一跳。「隨便啦。」他們嘟囔著走了。小寶擦擦眼睛。「你……留下來了。謝謝你。」',
        tellReply: '一位大人走過來，輕聲跟那兩個大孩子說話。他們向小寶道歉了。告訴大人不是打小報告——是在找人幫忙。',
        ignoreReply: '你走開了。背後的笑聲沒有停。整個下午，你的肚子都覺得沉甸甸的。',
        boThanks: '你是真正的朋友。要不要跟我一起放風箏？',
      },
      contest: {
        notice: '🐝 下午兩點班級拼字比賽！到公布欄報名參加。',
        intro: '歡迎來到班級拼字比賽！一共三個單字。仔細聽，選出正確的拼法。',
        join: '🐝 參加拼字比賽',
        notYet: '拼字比賽下午兩點開始。',
        over: '今天的拼字比賽已經結束了。',
        word: (n: number) => `第 ${n} 個單字，共 3 個。仔細聽……🔊`,
        hear: '🔊 再聽一次',
        right: '✅ 答對了！',
        wrong: (w: string) => `❌ 哎呀——正確拼法是「${w}」。`,
        win: '🏅 你贏得了拼字比賽！林老師在你的衣服上別了一隻金色蜜蜂。大家都在拍手！',
        setback: '這次你得了第四名。你的朋友小美靠「necessary」這個字贏了。',
        mindsetQ: '林老師：「你覺得怎麼樣？」',
        mindsetGood: '我會練習，下次再挑戰！',
        mindsetBad: '我就是不會拼字。',
        mindsetBadReply: '你不是不會拼字——你只是「還」不太會。每個高手都曾經是初學者。下次你可以試試什麼方法？',
        mindsetReply: '就是這樣！每天練習一點點，真的有用。',
        winReply: '恭喜你！記得：你是靠練習走到這裡的。繼續加油！',
        /** 英文練習：拼字單字，保留英文。 */
        words: ['because', 'friend', 'beautiful', 'library', 'Wednesday', 'different', 'tomorrow', 'favourite', 'believe', 'together', 'surprise', 'answer'],
      },
      quiz: {
        start: '隨堂小考時間！別擔心——只是看看我們學了多少。盡力就好！',
        go: '📝 開始小考',
        result: (g: string) => (g === 'A' ? '表現太棒了！拿到 A！' : g === 'B' ? '做得好——拿到 B！再多練習一點，你就會飛起來。' : '這次是 C。這只是告訴我們該練習什麼。我們一起慢慢來。'),
      },
      parentsMeeting: {
        notice: '👨‍👩‍👧 今天晚上有家長會！放學後媽媽會跟林老師見面。',
        momIntro: '我從家長會回來了。林老師跟我說了好多你的事……',
        great: '她說你很善良，常常幫助同學。她跟我說了這些時刻：',
        proud: '我真以你為榮！',
        study: '她也說你讀書很認真。繼續加油！',
        studyLow: '她說你可以再多專心讀書一點。我們一起訂個計畫好嗎？',
        unkind: '她提到有一次你對同學不太友善。你覺得那位同學會有什麼感覺？',
        reflect: '他們可能很難過。我會去道歉。',
        reflectReply: '這才是我的孩子。道歉是很勇敢的事。',
        thanksMom: '謝謝媽媽！❤️',
        plan: '我們一起訂個讀書計畫吧。',
        planReply: '很好。每天晚上二十分鐘——我來泡茶。一言為定？',
        nothing: '她說你是個很可愛的孩子，很期待看到你更多的表現！',
        done: '媽媽給了你一個大大的擁抱。',
      },
    },
  },
};
